import { Row, Col } from "react-bootstrap";
import AdminLinksComponent from "../../../components/admin/AdminLinksComponent";
import { TableHeader, Pagination, Search } from "../../../components/DataTable";
import { useEffect, useState, useMemo } from "react";
import "./invoicePDF.css";
import * as XLSX from "sheetjs-style";
import * as FileSaver from "file-saver";

const ProductsCountComponent = ({ getOrders, deleteOrder }) => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "productName", order: "asc" });
  const ITEMS_PER_PAGE = 40;

  const headers = [
    { name: "No#", field: "index", sortable: false },
    { name: "Product Name", field: "productName", sortable: true },
    { name: "Supplier", field: "supplier", sortable: true },
    { name: "CTL SKU", field: "ctlsku", sortable: true },
    { name: "Customer", field: "userNames", sortable: true },
    { name: "Company", field: "userCompanies", sortable: true },
    { name: "PO Number", field: "purchaseNumbers", sortable: true },
    { name: "Sold", field: "totalQuantity", sortable: true },
    { name: "Stock", field: "currentStock", sortable: true },
    { name: "Dates", field: "createdAtList", sortable: true },
  ];

  const columnWidths = ["3%", "30%", "10%", "8%", "10%", "13%", "8%", "5%", "5%", "10%"];

  const todayObj = new Date();
  todayObj.setMinutes(todayObj.getMinutes() - todayObj.getTimezoneOffset());
  const today = todayObj.toISOString().split("T")[0];

  const [selectedCompany, setSelectedCompany] = useState();
  const [startDate, setStartDate] = useState("2023-01-01");
  const [endDate, setEndDate] = useState(today);
  const [companies, setCompanies] = useState([]);
  const [productStockMap, setProductStockMap] = useState({});

  useEffect(() => {
    getOrders()
      .then((orders) => setOrders(orders))
      .catch((er) =>
        console.log(
          er?.response?.data?.message
            ? er.response.data.message
            : er?.response?.data || er.message
        )
      );
  }, [getOrders]);

  const getCompaniesFromOrders = (orders) => {
    return [...new Set(orders.map((order) => order.userCompany).filter(Boolean))].sort();
  };

  const filterOrders = (orders, selectedCompany, startDate, endDate) => {
    const start = startDate ? new Date(`${startDate}T00:00:00.000Z`) : null;
    const end = endDate ? new Date(`${endDate}T23:59:59.999Z`) : null;

    return orders.filter((order) => {
      const matchesCompany = selectedCompany ? order.userCompany === selectedCompany : true;
      const orderDate = new Date(order.createdAt);
      const matchesStart = start ? orderDate >= start : true;
      const matchesEnd = end ? orderDate <= end : true;

      return matchesCompany && matchesStart && matchesEnd;
    });
  };

  const createCombinedProductArray = (orders) => {
    const productSummaryMap = new Map();

    orders.forEach((order) => {
      const {
        userCompany,
        userName,
        deliverySite,
        purchaseNumber,
        createdAt,
        cartItems = [],
      } = order;

      const shortDate = createdAt
        ? new Date(createdAt).toISOString().split("T")[0]
        : "";

      cartItems.forEach((item) => {
        const productName = item.name || "";
        const productId = item.productId || "";

        (item.cartProducts || []).forEach((cp) => {
          const quantity = Number(cp.suppliedQty || 0);
          const ctlsku = cp.ctlsku || "";
          const key = `${productName}__${ctlsku}`;

          if (!productSummaryMap.has(key)) {
            productSummaryMap.set(key, {
              productName,
              ctlsku,
              totalQuantity: quantity,
              purchaseNumbers: purchaseNumber ? [purchaseNumber] : [],
              userCompanies: userCompany ? [userCompany] : [],
              userNames: userName ? [userName] : [],
              deliverySites: deliverySite ? [deliverySite] : [],
              createdAtList: shortDate ? [shortDate] : [],
              productId,
            });
          } else {
            const existing = productSummaryMap.get(key);
            existing.totalQuantity += quantity;

            if (userCompany && !existing.userCompanies.includes(userCompany)) {
              existing.userCompanies.push(userCompany);
            }

            if (userName && !existing.userNames.includes(userName)) {
              existing.userNames.push(userName);
            }

            if (purchaseNumber && !existing.purchaseNumbers.includes(purchaseNumber)) {
              existing.purchaseNumbers.push(purchaseNumber);
            }

            if (deliverySite && !existing.deliverySites.includes(deliverySite)) {
              existing.deliverySites.push(deliverySite);
            }

            if (shortDate && !existing.createdAtList.includes(shortDate)) {
              existing.createdAtList.push(shortDate);
            }
          }
        });
      });
    });

    return Array.from(productSummaryMap.values());
  };

  useEffect(() => {
    if (orders?.length) {
      setCompanies(getCompaniesFromOrders(orders));
    }
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return filterOrders(orders || [], selectedCompany, startDate, endDate);
  }, [orders, selectedCompany, startDate, endDate]);

  const filteredProductSummary = useMemo(() => {
    return createCombinedProductArray(filteredOrders);
  }, [filteredOrders]);

  const stockMap = useMemo(() => {
    const map = {};
    Object.values(productStockMap || {}).forEach((p) => {
      map[p.id] = {
        stockCount: p.stockCount,
        supplier: p.supplier,
      };
    });
    return map;
  }, [productStockMap]);

  const enrichedProductSummary = useMemo(() => {
    return filteredProductSummary.map((item) => {
      const stockData = stockMap[item.productId];
      return {
        ...item,
        currentStock: stockData?.stockCount ?? 0,
        supplier: stockData?.supplier ?? null,
      };
    });
  }, [filteredProductSummary, stockMap]);

  // console.log(enrichedProductSummary);

  const productIds = useMemo(() => {
    return [...new Set(
      filteredProductSummary
        .map((item) => item.productId)
        .filter(Boolean)
    )];
  }, [filteredProductSummary]);

  const searchedData = useMemo(() => {
    if (!search.trim()) return enrichedProductSummary;

    const lower = search.toLowerCase();

    return enrichedProductSummary.filter((item) => {
      const searchableText = [
        item.productName,
        item.ctlsku,
        item.totalQuantity,
        item.currentStock,
        item.supplier,
        ...(item.userNames || []),
        ...(item.userCompanies || []),
        ...(item.deliverySites || []),
        ...(item.purchaseNumbers || []),
        ...(item.createdAtList || []),
      ]
        .join(" ")
        .toLowerCase();

      return searchableText.includes(lower);
    });
  }, [enrichedProductSummary, search]);

  const sortedData = useMemo(() => {
    const data = [...searchedData];
    const { field, order } = sorting;

    data.sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      if (Array.isArray(aValue)) aValue = aValue.join(", ");
      if (Array.isArray(bValue)) bValue = bValue.join(", ");

      if (field === "totalQuantity" || field === "currentStock") {
        aValue = Number(aValue || 0);
        bValue = Number(bValue || 0);
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      aValue = (aValue || "").toString().toLowerCase();
      bValue = (bValue || "").toString().toLowerCase();

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });

    return data;
  }, [searchedData, sorting]);

  const totalItems = sortedData.length;

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedData, currentPage]);

  console.log(paginatedData);

  useEffect(() => {
    const fetchProductStocks = async () => {
      try {
        if (!productIds.length) {
          setProductStockMap({});
          return;
        }

        const res = await fetch("/api/products/stock-counts", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ productIds }),
        });

        const data = await res.json();
        setProductStockMap(data || {});
      } catch (error) {
        console.error("Failed to fetch product stock counts:", error);
        setProductStockMap({});
      }
    };

    fetchProductStocks();
  }, [productIds]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedCompany, startDate, endDate, sorting]);


  const exportToExcel = () => {
    const exportData = sortedData.map((item, index) => ({
      "No#": index + 1,
      "Product Name": item.productName || "",
      "CTL SKU": item.ctlsku || "",
      "Customer": (item.userNames || [])
        .map((un) => un.split("(")[0].trim())
        .join(", "),
      "Company": (item.userCompanies || []).join(", "),
      "Delivery Site": (item.deliverySites || []).join(", "),
      "PO Number": (item.purchaseNumbers || []).join(", "),
      "Sold": item.totalQuantity || 0,
      "Stock": item.currentStock ?? 0,
      "Dates": (item.createdAtList || []).join(", "),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const columnWidths = [
      { wch: 6 },   // No#
      { wch: 45 },  // Product Name
      { wch: 15 },  // CTL SKU
      { wch: 25 },  // Customer
      { wch: 20 },  // Company
      { wch: 18 },  // Delivery Site
      { wch: 18 },  // PO Number
      { wch: 10 },  // Count
      { wch: 20 },  // Dates
    ];
    worksheet["!cols"] = columnWidths;

    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!worksheet[cellAddress]) continue;

      worksheet[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "1F4E78" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "D9D9D9" } },
          bottom: { style: "thin", color: { rgb: "D9D9D9" } },
          left: { style: "thin", color: { rgb: "D9D9D9" } },
          right: { style: "thin", color: { rgb: "D9D9D9" } },
        },
      };
    }

    for (let R = 1; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;

        worksheet[cellAddress].s = {
          alignment: { vertical: "top", wrapText: true },
          border: {
            top: { style: "thin", color: { rgb: "EAEAEA" } },
            bottom: { style: "thin", color: { rgb: "EAEAEA" } },
            left: { style: "thin", color: { rgb: "EAEAEA" } },
            right: { style: "thin", color: { rgb: "EAEAEA" } },
          },
        };
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products Count");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    const fileName = `products_count_${selectedCompany || "all_companies"}_${startDate}_to_${endDate}.xlsx`;
    FileSaver.saveAs(data, fileName);
  };

  return (
    <Row className="content-container m-5">
      <Col md={2}>
        <AdminLinksComponent />
      </Col>

      <Col md={10}>
        <div className="row mb-3">
          <div className="col-md-5" style={{ width: "30%" }}>
            <h1>PRODUCTS SOLD</h1>
          </div>
        </div>

        <div className="d-flex gap-2 flex-wrap mb-3">
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="form-select"
            style={{ maxWidth: "240px" }}
          >
            <option value="">All Companies</option>
            {companies.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="form-control"
            style={{ maxWidth: "180px" }}
          />

          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="form-control"
            style={{ maxWidth: "180px" }}
          />
        </div>

        <div className="row align-items-center mb-3">
          <div className="col-md-4">
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>

          <div className="col-md-2">
            <div style={{ fontWeight: 600 }}>Total Records: {totalItems}</div>
          </div>

          <div className="col-md-2 d-flex justify-content-center">
            <button className="btn btn-success" onClick={exportToExcel}>
              Export to Excel
            </button>
          </div>

          <div className="col-md-4 d-flex justify-content-end">
            <Search
              onSearch={(value) => {
                setSearch(value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <table className="table table-striped table-sm">
          <TableHeader
            headers={headers}
            onSorting={(field, order) => {
              setSorting({ field, order });
              setCurrentPage(1);
            }}
            widths={columnWidths}
          />

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item, index) => (
                <tr key={`${item.productName}-${item.ctlsku}-${index}`}>
                  <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>

                  <td><a href={`/product-details/${item.productId}`}>{item.productName}</a></td>
                  <td><a href={`/product-details/${item.productId}`}>{item.supplier}</a></td>
                  <td><a href={`/product-details/${item.productId}`}>{item.ctlsku}</a></td>
                  <td>
                    {item.userNames?.map((un, i) => (
                      <div key={i}>{un.split("(")[0].trim()}</div>
                    ))}
                  </td>
                  <td>
                    <div>{item.userCompanies?.map((uc, i) => (
                      <div key={i}>{uc}</div>
                    ))}</div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {item.deliverySites?.join(", ")}
                    </div>
                  </td>
                  <td>
                    {item.purchaseNumbers?.map((pn, i) => (
                      <div key={i}>{pn}</div>
                    ))}
                  </td>
                  <td style={{ fontWeight: "bold", color: "#0d6efd" }}>
                    {item.totalQuantity}
                  </td>
                  <td style={{ fontWeight: "bold", color: "#198754" }}>
                    {item.currentStock}
                  </td>
                  <td>
                    {item.createdAtList?.map((date, i) => (
                      <div key={i}>{date}</div>
                    ))}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="col-md-5 mt-3">
          <Pagination
            total={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </Col>
    </Row>
  );
};

export default ProductsCountComponent;